import React from 'react';
import Icon, { IconName } from '../../Icon';
import Text, { TextColor } from '../../Text';
import { Shadow, colors, shadows } from '../../../../utils/theme';
import { StateStyles, useInteractionStateStyles } from '../../../../hooks/useInteractionStateStyles';
import Stack from '../../Stack';
import { LayoutChangeEvent, Pressable } from 'react-native';
import { useIsDisabledByParent } from '../../IsDisabledProvider';
import Popover from '../../Popover';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import Divider from '../../Divider';

export type TableActionButtonOwnProps = {
    title: string;
    icon: IconName;
    onPress: () => void;
    testID?: string;
    isDisabled?: boolean;
};

export type TableActionButtonProps = TableActionButtonOwnProps;

export type TableActionGroupAction = {
    title: string;
    onPress: () => void;
    testID?: string;
    isDisabled?: boolean;
};

export type TableActionGroupButtonProps = {
    title: string;
    icon: IconName;
    testID?: string;
    isDisabled?: boolean;
    actions: TableActionGroupAction[];
};

type StateStyle = {
    background: string;
    text: TextColor;
    borderColor?: string;
    shadow?: Shadow;
};

const stateStyles: StateStyles<StateStyle> = {
    default: {
        background: colors['Secondary-Button-Default'],
        text: 'Dark-Text',
        borderColor: colors['Dark-Text'],
        shadow: shadows.default,
    },
    hovered: {
        background: colors['Secondary-Button-Hover'],
        text: 'Gray-Text',
        borderColor: colors['Secondary-Button-Hover-Border'],
    },
    pressed: {
        background: colors['Secondary-Button-Press'],
        text: 'Dark-Text',
        borderColor: colors['Dark-Text'],
    },
    inactive: {
        background: colors['Secondary-Button-Inactive'],
        text: 'Inactive-Text',
        borderColor: colors['Grey-Light'],
    },
};

const SharedButton: React.FC<
    TableActionButtonProps & { buttonRef?: any; onLayout?: (event: LayoutChangeEvent) => void; isGroup: boolean }
> = (props) => {
    const isDisabled = useIsDisabledByParent(props.isDisabled);
    const { styles, onPressIn, onPressOut, renderInHoverContainer } = useInteractionStateStyles({
        stateStyles,
        isDisabled,
    });

    return renderInHoverContainer(
        <Pressable
            disabled={isDisabled}
            onPressIn={onPressIn}
            onPressOut={onPressOut}
            onPress={isDisabled ? undefined : props.onPress}
            testID={props.testID ? `table-action-${props.testID}` : undefined}
            ref={props.buttonRef}
            onLayout={props.onLayout}
        >
            <Stack
                horizontal
                space={2}
                align="center"
                style={{
                    borderColor: styles.borderColor,
                    borderWidth: 1,
                    borderRadius: 7,
                    paddingLeft: 6,
                    paddingRight: 6,
                    height: 42,
                }}
            >
                <Icon name={props.icon} size="table-action-icon" color={styles.text} />
                <Text variant="button" color={styles.text}>
                    {props.title}
                </Text>
                {props.isGroup ? <Icon name={'ChevronDown'} size="table-action-icon" color={styles.text} /> : null}
            </Stack>
        </Pressable>,
    );
};

export const TableActionButton: React.FC<TableActionButtonProps> = (props) => {
    return <SharedButton {...props} isGroup={false} />;
};

export const TableActionGroupButton: React.FC<TableActionGroupButtonProps> = (props) => {
    return (
        <Popover
            renderButton={({ onPress, ref, onLayout, isDisabled }) => (
                <SharedButton
                    {...props}
                    onPress={onPress}
                    buttonRef={ref}
                    onLayout={onLayout}
                    isGroup={true}
                    isDisabled={isDisabled}
                />
            )}
            popoverPosition="top-left-align"
            popoverWidth={160}
        >
            {(onClose) => (
                <Stack vertical fullWidth padding={4} space={4}>
                    {props.actions.map((action, index) => (
                        <React.Fragment key={`${action.title}-${index}`}>
                            <PressableDynamicOpacity
                                onPress={() => {
                                    onClose();
                                    action.onPress();
                                }}
                                key={action.title}
                                isDisabled={action.isDisabled}
                                style={{ width: '100%' }}
                                testID={action.testID ? `table-action-${action.testID}` : undefined}
                            >
                                <Stack horizontal space={4} align="center" justify="flex-start" fullWidth>
                                    <Text variant="bold-body-1">{action.title}</Text>
                                </Stack>
                            </PressableDynamicOpacity>
                            {index < props.actions.length - 1 && <Divider horizontal />}
                        </React.Fragment>
                    ))}
                </Stack>
            )}
        </Popover>
    );
};
